import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock, Home } from "lucide-react";
import { CHAPTERS } from "../data/content";
import { Chapter } from "../components/Chapter";
import { LessonMenu } from "../components/LessonMenu";
import { EmailGate } from "../components/EmailGate";
import { useAccess, isGated } from "../context/AccessContext";

export default function LessonPage() {
  const { chapterId } = useParams();
  const { unlocked } = useAccess();

  const idx = CHAPTERS.findIndex((c) => c.id === chapterId);
  if (idx < 0) return <Navigate to="/learn/c0" replace />;

  const chapter = CHAPTERS[idx];
  const prev = CHAPTERS[idx - 1];
  const next = CHAPTERS[idx + 1];
  const locked = isGated(chapterId) && !unlocked;
  const nextLocked = next && isGated(next.id) && !unlocked;

  return (
    <main className="max-w-[1400px] mx-auto px-5 md:px-10 relative z-10">
      <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
        <LessonMenu currentId={chapterId} />

        <div className="pb-40 pt-8 md:pt-12 min-h-screen">
          {/* mobile top bar */}
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <Link to="/" className="font-mono-ui text-[11px] uppercase tracking-[0.16em] text-[#1C2430]/60 flex items-center gap-1.5" data-testid="mobile-home-link">
              <Home size={13} /> Guide
            </Link>
            <span className="font-mono-ui text-[11px] tabular-nums text-[#1C2430]/45">
              {chapter.num} / {CHAPTERS[CHAPTERS.length - 1].num}
            </span>
          </div>

          {locked ? (
            <EmailGate nextTitle={`${chapter.num} · ${chapter.title}`} />
          ) : (
            <motion.div key={chapterId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <Chapter chapter={chapter} index={idx} />

              <div className="mt-16 pt-8 border-t border-[var(--border-heavy)] flex items-stretch justify-between gap-4" data-testid="lesson-nav">
                {prev ? (
                  <Link to={`/learn/${prev.id}`} data-testid="lesson-prev" className="group flex-1 max-w-[46%] border border-[var(--border-heavy)] p-4 hover:border-[#1C2430] transition-colors">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-[#1C2430]/45 flex items-center gap-1.5 mb-1"><ArrowLeft size={12} /> Previous</span>
                    <span className="font-serif-display text-[18px] text-[#1C2430] leading-tight block">{prev.num} · {prev.title}</span>
                  </Link>
                ) : (
                  <Link to="/" data-testid="lesson-prev" className="flex-1 max-w-[46%] border border-[var(--border-heavy)] p-4 hover:border-[#1C2430] transition-colors">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-[#1C2430]/45 flex items-center gap-1.5 mb-1"><Home size={12} /> Back to</span>
                    <span className="font-serif-display text-[18px] text-[#1C2430] leading-tight block">The cover</span>
                  </Link>
                )}

                {next ? (
                  <Link to={`/learn/${next.id}`} data-testid="lesson-next" className="group flex-1 max-w-[46%] border border-[#1C2430] bg-[#1C2430] text-[#FBFBF9] p-4 hover:bg-[#364152] transition-colors text-right">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-[#FBFBF9]/55 flex items-center justify-end gap-1.5 mb-1">
                      Next {nextLocked ? <Lock size={11} /> : <ArrowRight size={12} />}
                    </span>
                    <span className="font-serif-display text-[18px] leading-tight block">{next.num} · {next.title}</span>
                  </Link>
                ) : (
                  <Link to="/" data-testid="lesson-next" className="flex-1 max-w-[46%] border border-[#2F6F4F] bg-[#2F6F4F] text-[#FBFBF9] p-4 hover:bg-[#245c41] transition-colors text-right">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-[#FBFBF9]/60 block mb-1">You finished the guide</span>
                    <span className="font-serif-display text-[18px] leading-tight block">Back to the cover ↑</span>
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </main>
  );
}
